import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useStoreContext } from 'features/store';
import _ from 'lodash';

import { useCart } from 'hooks/use-cart';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useLoyaltyContext } from 'state/loyalty';
import {
  compareBy_id,
  compareBy_idAndErrors,
  useConditionalState,
} from 'state/loyalty/hooks/use-conditional-state';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { SanityOfferListItem, isSanityOfferListItem } from 'state/loyalty/types';
import { useMainMenuContext } from 'state/menu/main-menu';
import { IDayPartBoundary } from 'state/menu/main-menu/types';
import { useServiceModeContext } from 'state/service-mode';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';

import {
  TransformTierGroupsToOffersOptions_Legacy,
  transformTierGroupsToOffers_Legacy,
} from './tiered-offers/legacy';
import {
  TransformTierGroupsToOffersOptions_UnifiedMenu,
  transformTierGroupsToOffers_UnifiedMenu,
} from './tiered-offers/one-menu';

export function useGetCmsAndLoyaltyOffers() {
  const [refreshOffersLoading, setRefreshOffersLoading] = useState(false);
  const {
    queryLoyaltyOffers,
    queryLoyaltyUserStandardOffers,
    loyaltyStandardOffersEnabled,
    refetchCmsOffers,
  } = useLoyaltyContext();
  const { loyaltyUserId, loading: loyaltyUserIdLoading } = useLoyaltyUserId();
  const { calculateCartTotal } = useCart();
  const dispatch = useAppDispatch();
  const { activeDayParts } = useMainMenuContext();
  const dayPartsRef = useRef({
    setActiveDayPartOnLoad: false,
    prevDayParts: [] as IDayPartBoundary[],
  });

  const sanityOfferListItemsWithExtras = useAppSelector(selectors.loyalty.selectCmsOffers);
  const [sanityOfferListItems, setSanityOfferListItems] = useConditionalState(
    () => sanityOfferListItemsWithExtras.filter(isSanityOfferListItem),
    compareBy_id
  );
  const { serviceMode } = useServiceModeContext();
  const { store, prices } = useStoreContext();
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { indexedMenu, isLoading: isUnifiedMenuLoading } = useUnifiedMenuContext();

  const userEngineOfferListItems = useAppSelector(selectors.loyalty.selectEngineOfferListItems);

  const _appPersonalizedOffers = useAppSelector(selectors.loyalty.selectAppPersonalizedOffers);
  const [appPersonalizedOffers, setAppPersonalizedOffers] = useConditionalState(
    _appPersonalizedOffers,
    compareBy_idAndErrors
  );
  const guestEngineOfferListItems = useAppSelector(
    selectors.loyalty.selectGuestEngineOfferListItems
  );

  const transformedSanityOffers = useMemo(() => {
    if (isUnifiedMenuEnabled && !isUnifiedMenuLoading && indexedMenu && serviceMode) {
      return transformTierGroupsToOffers_UnifiedMenu({
        items: sanityOfferListItemsWithExtras as TransformTierGroupsToOffersOptions_UnifiedMenu['items'],
        indexedMenu,
        serviceMode,
      });
    } else if (!isUnifiedMenuEnabled && store._id && prices && serviceMode) {
      return transformTierGroupsToOffers_Legacy({
        items: sanityOfferListItemsWithExtras as TransformTierGroupsToOffersOptions_Legacy['items'],
        store,
        serviceMode,
        prices,
      });
    }
    return [];
  }, [
    sanityOfferListItemsWithExtras,
    isUnifiedMenuEnabled,
    isUnifiedMenuLoading,
    indexedMenu,
    serviceMode,
    store,
    prices,
  ]);

  useEffect(() => {
    // @ts-expect-error TS2345 - transformedSanityOffers is SanityOfferListItem[]
    setSanityOfferListItems(transformedSanityOffers);
  }, [transformedSanityOffers, setSanityOfferListItems]);

  useEffect(() => {
    setAppPersonalizedOffers(_appPersonalizedOffers);
  }, [_appPersonalizedOffers, setAppPersonalizedOffers]); // setPersonalizedOffers is stable but ESLint doesn't know that

  const engineOfferListItems =
    !loyaltyUserIdLoading && loyaltyUserId ? userEngineOfferListItems : guestEngineOfferListItems;

  // This refers to userOffers, personalizedOffers, cmsOffers and unauthenticatedOffers
  const offersLoading = useAppSelector(selectors.loyalty.selectOffersLoading);

  const cartTotal: number = useMemo(() => (calculateCartTotal ? calculateCartTotal() : 0), [
    calculateCartTotal,
  ]);

  const fetchOffers = useCallback(() => {
    // Reset shouldRefetchOffers flag
    dispatch(actions.loyalty.setShouldRefetchOffers(false));

    if (loyaltyStandardOffersEnabled) {
      if (loyaltyUserId) {
        queryLoyaltyUserStandardOffers({ loyaltyId: loyaltyUserId, subtotalAmount: cartTotal });
      } else {
        queryLoyaltyOffers({ subtotalAmount: cartTotal });
      }
    }
  }, [
    cartTotal,
    loyaltyUserId,
    loyaltyStandardOffersEnabled,
    queryLoyaltyOffers,
    queryLoyaltyUserStandardOffers,
    dispatch,
  ]);

  const refreshOffers = useCallback(async () => {
    // When a user manually refreshes we have to fetch the CMS offers
    // and the engine offers. These two offers have been implemented in different
    // ways and signal different loading mechanisms (one is through redux, the other a hook)
    // To standardize the way this component responds to loading, I've opted to
    // manually set the offers loading (which is currently what happens when you call fetchOffers)
    // I'm doing this eagerly so the refresh control doesn't close. FlatList will close the RefreshControl
    // if the refreshing state doesn't get signalled in an appropriate time.

    setRefreshOffersLoading(true);

    await Promise.all([refetchCmsOffers]).then(fetchOffers);

    setRefreshOffersLoading(false);
  }, [fetchOffers, refetchCmsOffers]);

  useEffect(() => {
    if (activeDayParts.length === 0) {
      return;
    }

    if (!_.isEqual(activeDayParts, dayPartsRef.current.prevDayParts)) {
      if (!dayPartsRef.current.setActiveDayPartOnLoad) {
        dayPartsRef.current.setActiveDayPartOnLoad = true;
      } else {
        refreshOffers();
      }

      dayPartsRef.current.prevDayParts = activeDayParts;
    }
  }, [activeDayParts, refreshOffers]);

  return {
    sanityOfferListItems: sanityOfferListItems as SanityOfferListItem[],
    appPersonalizedOffers,
    engineOfferListItems,
    offersLoading,
    refreshOffers,
    refreshOffersLoading,
  };
}
